/**
 * Step building blocks shared by every RFIs tour
 * (``list.ts``, ``detail.ts``, ``combined.ts``).
 *
 * The detail view here has more surface area than punch-lists or
 * observations — RFIs live in a workflow (Draft → Open → Answered →
 * Closed), with separate cards for the question, the official response,
 * comment threads, attachments, and quick assignment. The tour walks
 * each of those in turn so a new user has a mental map of where to look.
 */
import type { TourStep } from '../types';
import { clickPreferReact, has, waitForCondition } from '../utils/dom';

// ---------------------------------------------------------------------------
// Selectors / predicates
// ---------------------------------------------------------------------------

/** True once the RFI detail page is mounted in the DOM. */
export const hasDetailMounted = (): boolean =>
  has('[data-tour-id="rfi-detail-header-actions"]');

// ---------------------------------------------------------------------------
// List-side steps
// ---------------------------------------------------------------------------

export const newRfiStep: TourStep = {
  id: 'new-rfi',
  target: '[data-tour-id="rfis-new"]',
  placement: 'left',
  title: 'Log a new RFI',
  body:
    'Click here to start a new Request For Information — a question to ' +
    'designers, a clarification, anything that needs an answer.',
};

export const widgetsStep: TourStep = {
  id: 'widgets',
  target: '[data-tour-id="rfis-widgets"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'At-a-glance counts',
  body:
    'How many RFIs are still open, how many are overdue, and how many ' +
    'are waiting on your response specifically.',
};

export const filtersStep: TourStep = {
  id: 'filters',
  target: '[data-tour-id="rfis-filters"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Find what you need',
  body:
    'Search by number, title, or drawing reference — and filter by ' +
    'status, priority, responsible party, or overdue-only.',
};

/** "Open an RFI" — list-only flavor (description, no row click). */
export const listTableStep: TourStep = {
  id: 'table',
  target: '[data-tour-id="rfis-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open an RFI',
  body: 'Each row is one RFI. Click any row to open the full details.',
};

/**
 * "Open an RFI" — combined flavor: clicking Next opens the first row
 * and waits for the detail page to mount.
 */
export const combinedTableStep: TourStep = {
  id: 'open-an-rfi',
  target: '[data-tour-id="rfis-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open an RFI',
  body:
    'Click any row to open it. Press Next and we\u2019ll open the first ' +
    'one for you.',
  onBeforeNext: async () => {
    clickPreferReact('[data-tour-id="rfis-table"] tbody tr');
    await waitForCondition(hasDetailMounted, 4000, 'row click → detail page');
  },
  autoAdvanceWhen: () => hasDetailMounted(),
};

// ---------------------------------------------------------------------------
// Detail-side steps
// ---------------------------------------------------------------------------

export const headerActionsStep: TourStep = {
  id: 'header-actions',
  target: '[data-tour-id="rfi-detail-header-actions"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Status & workflow',
  body:
    'The current status, workflow buttons (Submit, Close, Reopen), and ' +
    'the Edit button live here. Jump straight to the 360\u00B0 hotspot ' +
    'if one is attached.',
};

export const metadataStep: TourStep = {
  id: 'metadata',
  target: '[data-tour-id="rfi-detail-metadata"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Key fields',
  body:
    'Quick summary — priority, due date, responsible party, assignee, ' +
    'and whose court the ball is currently in.',
};

export const questionStep: TourStep = {
  id: 'question',
  target: '[data-tour-id="rfi-detail-question"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'The question',
  body:
    'What\u2019s being asked, plus context — location, drawing reference, ' +
    'spec section, and any suggested solution from the requester.',
};

export const responseStep: TourStep = {
  id: 'response',
  target: '[data-tour-id="rfi-detail-response"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Official response',
  body:
    'Where the official answer goes. Once you\u2019ve written one you ' +
    'can mark the RFI as Answered, or Respond + Close in a single step.',
};

export const commentsStep: TourStep = {
  id: 'comments',
  target: '[data-tour-id="rfi-detail-comments"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Comments & activity',
  body:
    'Back-and-forth threads — useful for clarifications without changing ' +
    'the official response.',
};

export const attachmentsStep: TourStep = {
  id: 'attachments',
  target: '[data-tour-id="rfi-detail-attachments"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Attachments',
  body: 'Upload supporting files — drawings, specs, photos.',
};

export const assignmentStep: TourStep = {
  id: 'assignment',
  target: '[data-tour-id="rfi-detail-assignment"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Quick assignment',
  body:
    'Reassign the responsible party or assignee right here without ' +
    'opening the full edit dialog.',
};
